import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { DocumentText, Import } from 'iconsax-react';
import { ClientContractStatus } from '@application/common/enums/client-contract-status';
import { formatDateDayMonthYear } from '@infrastructure/helpers';
// ── presentation helpers (only fields already present on AssignmentDetailDto / UserDocumentsDetailDto) ──
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
/** Stored file names are generated (`<uuid>.pdf`, `<client>_<name>_<uuid>.pdf`); never show those to the user. */
export const isGeneratedName = (name) => !name || UUID_PATTERN.test(name);
export const contractPeriod = (doc) => {
    const start = doc.contactStartDate ? formatDateDayMonthYear(doc.contactStartDate) : '';
    const end = doc.contactEndDate ? formatDateDayMonthYear(doc.contactEndDate) : '';
    if (start && end)
        return `${start} – ${end}`;
    if (start)
        return `From ${start}`;
    if (end)
        return `Until ${end}`;
    return '';
};
export const contractTitle = (doc) => doc.clientName ? `Contract · ${doc.clientName}` : 'Contract';
/** Secondary line of a contract file row: period, plus the file's own label when an assignment has several files. */
export const contractSecondary = (doc, index, count) => {
    const period = contractPeriod(doc);
    if (count <= 1 && isGeneratedName(doc.name))
        return period;
    const label = isGeneratedName(doc.name) ? `Document ${index + 1}` : doc.name;
    return period ? `${period} · ${label}` : label;
};
/** Human title for an uploaded user document (KvK / other); generated names are replaced. */
export const userDocumentTitle = (name, fallback, index, count) => isGeneratedName(name) ? (count > 1 ? `${fallback} ${index + 1}` : fallback) : name;
/** Groups the flat per-file list by assignment, keeping API order. */
export const groupByAssignment = (docs) => {
    const groups = new Map();
    docs.forEach((doc, index) => {
        const key = doc.assignmentId || `unknown-${index}`;
        const files = groups.get(key) || [];
        if (!files.some((f) => f.folderName === doc.folderName && f.name === doc.name && f.autoGeneratedContractLink === doc.autoGeneratedContractLink)) {
            files.push(doc);
        }
        groups.set(key, files);
    });
    return Array.from(groups.values());
};
const isWithinPeriod = (doc) => {
    const now = Date.now();
    const started = !doc.contactStartDate || new Date(doc.contactStartDate).getTime() <= now;
    const notEnded = !doc.contactEndDate || new Date(doc.contactEndDate).getTime() >= now;
    return started && notEnded;
};
export const contractBadge = (doc) => {
    switch (doc.status) {
        case ClientContractStatus.PendingSignature:
            return { label: 'To sign', color: '#960041', background: '#FFE3EF' };
        case ClientContractStatus.Signed:
            return { label: 'Awaiting approval', color: '#8A5600', background: '#FFF4E1' };
        case ClientContractStatus.Approved:
            return isWithinPeriod(doc)
                ? { label: 'Active', color: '#316F1D', background: '#E5FEDE' }
                : { label: 'Approved', color: '#1D5C84', background: '#D9F3FF' };
        case ClientContractStatus.Rejected:
            return { label: 'Declined', color: '#492A2A', background: '#FDBCBC' };
        case ClientContractStatus.Expired:
            return { label: 'Expired', color: '#616161', background: '#E0E0E0' };
        default:
            return undefined;
    }
};
export const StatusBadge = ({ badge }) => badge ? (_jsx(Box, { component: 'span', sx: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        px: 1,
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: 1,
        borderRadius: '20px',
        whiteSpace: 'nowrap',
        color: badge.color,
        backgroundColor: badge.background
    }, children: badge.label })) : null;
/**
 * Same look as the shared DownloadAction row, but the whole row is one keyboard-accessible button
 * and the redundant "Download" text is omitted (the icon stays).
 */
export const DocumentRow = ({ title, secondary, badge, muted = false, disabled = false, loading = false, width = '100%', actionLabel, onClick }) => {
    const isDisabled = disabled || loading;
    return (_jsxs(ButtonBase, { onClick: () => {
            if (!isDisabled)
                onClick();
        }, disabled: isDisabled, "aria-label": actionLabel || [`Open ${title}`, secondary, badge?.label].filter(Boolean).join(', '), "aria-busy": loading || undefined, focusRipple: true, sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            width,
            maxWidth: '100%',
            flexShrink: 0,
            textAlign: 'left',
            color: isDisabled ? '#A0A0A0' : muted ? '#616161' : 'black',
            background: isDisabled ? '#E0E0E0' : muted ? '#F0F0F0' : '#FFDD80',
            borderRadius: '13px',
            padding: '10px 15px',
            minHeight: 56,
            opacity: disabled ? 0.5 : 1,
            '&:hover': { backgroundColor: isDisabled ? '#E0E0E0' : muted ? '#E6E6E6' : '#FFC107' },
            '&.Mui-focusVisible': { outline: '2px solid #141414', outlineOffset: 2 }
        }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flexGrow: 1 }, children: [_jsx(DocumentText, { "aria-hidden": 'true', style: { flexShrink: 0 } }), _jsxs(Box, { sx: { minWidth: 0, flexGrow: 1 }, children: [_jsx(Typography, { variant: 'caption', component: 'div', color: 'inherit', fontWeight: 500, sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: title, children: title }), (secondary || badge) && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, minWidth: 0 }, children: [_jsx(StatusBadge, { badge: badge }), secondary && (_jsx(Typography, { variant: 'caption', color: 'inherit', sx: { opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: secondary, children: secondary }))] }))] })] }), _jsx(Box, { sx: { display: 'flex', alignItems: 'center', flexShrink: 0 }, "aria-hidden": 'true', children: loading ? _jsx(CircularProgress, { size: 20, color: 'inherit' }) : _jsx(Import, {}) })] }));
};
